import { useRef, useState } from 'react';
import './MediaCarousel.css';

// Swipeable media strip: native scroll-snap gives touch swiping on phones (and
// trackpad/shift-scroll), with prev/next arrows for mouse users on desktop.
//
// items: [{ type: 'image', src } | { type: 'video', thumb, url, label }]
// onImageClick(item, index) — index is the position in `items`.
export function MediaCarousel({ items, className = '', onImageClick }) {
  const trackRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const count = items.length;

  const handleScroll = () => {
    const el = trackRef.current;
    if (el && el.clientWidth) setIdx(Math.round(el.scrollLeft / el.clientWidth));
  };

  const goTo = (i) => {
    const el = trackRef.current;
    if (!el) return;
    const n = Math.max(0, Math.min(count - 1, i));
    el.scrollTo({ left: n * el.clientWidth, behavior: 'smooth' });
  };

  const handleKey = (e) => {
    if (e.key === 'ArrowLeft')  { e.preventDefault(); goTo(idx - 1); }
    if (e.key === 'ArrowRight') { e.preventDefault(); goTo(idx + 1); }
  };

  if (!count) return null;

  return (
    <div
      className={`mc ${className}`}
      tabIndex={count > 1 ? 0 : undefined}
      onKeyDown={count > 1 ? handleKey : undefined}
      aria-roledescription="carousel"
    >
      <div className="mc__track" ref={trackRef} onScroll={handleScroll}>
        {items.map((it, i) => (
          <div className="mc__slide" key={i} aria-label={`${i + 1} of ${count}`}>
            {it.type === 'video' ? (
              <a className="mc__video" href={it.url} target="_blank" rel="noopener noreferrer">
                <img src={it.thumb} alt="Video" loading="lazy" draggable={false} />
                <span className="mc__play">
                  <svg viewBox="0 0 24 24" fill="white" width="26" height="26"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                </span>
                <span className="mc__video-label">{it.label || 'Watch video'}</span>
              </a>
            ) : (
              <img
                className={onImageClick ? 'mc__img mc__img--click' : 'mc__img'}
                src={it.src}
                alt=""
                loading={i === 0 ? 'eager' : 'lazy'}
                draggable={false}
                onClick={onImageClick ? () => onImageClick(it, i) : undefined}
              />
            )}
          </div>
        ))}
      </div>

      {count > 1 && (
        <>
          <button
            type="button"
            className="mc__arrow mc__arrow--prev"
            onClick={() => goTo(idx - 1)}
            disabled={idx === 0}
            aria-label="Previous"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <button
            type="button"
            className="mc__arrow mc__arrow--next"
            onClick={() => goTo(idx + 1)}
            disabled={idx === count - 1}
            aria-label="Next"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <div className="mc__dots">
            {items.map((_, i) => (
              <button
                type="button"
                key={i}
                className={`mc__dot${i === idx ? ' mc__dot--active' : ''}`}
                onClick={() => goTo(i)}
                aria-label={`Go to ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
